import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Sparkle } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const ACCOUNT_TYPES = [
  { value: 'traditional_401k', label: 'Traditional 401(k)' },
  { value: 'traditional_ira', label: 'Traditional IRA' },
  { value: 'roth_401k', label: 'Roth 401(k)' },
  { value: 'roth_ira', label: 'Roth IRA' },
  { value: 'taxable_brokerage', label: 'Taxable Brokerage' },
  { value: 'cash', label: 'Cash Reserves' },
];

const RetirementPlanner = () => {
  const [accounts, setAccounts] = useState([]);
  const [formData, setFormData] = useState({
    account_type: '',
    balance: '',
    annual_contribution: ''
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/retirement-accounts');
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to load accounts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/retirement-accounts', {
        ...formData,
        balance: parseFloat(formData.balance),
        annual_contribution: parseFloat(formData.annual_contribution || 0)
      });
      toast.success('Account added!');
      setFormData({ account_type: '', balance: '', annual_contribution: '' });
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to add account');
    }
  };

  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);
  const totalContributions = accounts.reduce((sum, acc) => sum + acc.annual_contribution, 0);

  const chartData = ACCOUNT_TYPES.map(type => {
    const accs = accounts.filter(a => a.account_type === type.value);
    return {
      name: type.label,
      balance: accs.reduce((sum, a) => sum + a.balance, 0)
    };
  }).filter(d => d.balance > 0);

  return (
    <div className="space-y-8 fade-in">
      {/* Educational Disclaimer */}
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Tool:</strong> This planner helps track retirement accounts. For personalized retirement strategies, consult a qualified financial advisor or retirement planner.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Retirement Income Planner
        </h1>
        <p className="text-base text-[#4A5568]">
          Manage your retirement accounts and plan future income
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Total Retirement Balance</p>
          <p className="text-3xl font-semibold text-[#0A1128] ibm-plex">${totalBalance.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Annual Contributions</p>
          <p className="text-3xl font-semibold text-[#C5A059] ibm-plex">${totalContributions.toLocaleString()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Add Account Form */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="add-retirement-account-form">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Add Retirement Account</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="account_type">Account Type</Label>
              <Select value={formData.account_type} onValueChange={(value) => setFormData({ ...formData, account_type: value })} required>
                <SelectTrigger data-testid="account-type-select" className="border-slate-300">
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="balance">Current Balance ($)</Label>
              <Input
                id="balance"
                type="number"
                step="0.01"
                data-testid="balance-input"
                value={formData.balance}
                onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                className="border-slate-300"
                required
              />
            </div>

            <div>
              <Label htmlFor="annual_contribution">Annual Contribution ($)</Label>
              <Input
                id="annual_contribution"
                type="number"
                step="0.01"
                data-testid="contribution-input"
                value={formData.annual_contribution}
                onChange={(e) => setFormData({ ...formData, annual_contribution: e.target.value })}
                className="border-slate-300"
              />
            </div>

            <Button type="submit" data-testid="add-account-button" className="w-full bg-[#0A1128] hover:bg-[#152145]">
              <Plus size={18} className="mr-2" />
              Add Account
            </Button>
          </form>
        </div>

        {/* Account Distribution Chart */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="account-distribution-chart">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Account Distribution</h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#4A5568" angle={-45} textAnchor="end" height={100} style={{ fontSize: '11px' }} />
                <YAxis stroke="#4A5568" />
                <Tooltip />
                <Bar dataKey="balance" fill="#0A1128" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-[#4A5568] py-12">No accounts added yet</p>
          )}
        </div>
      </div>

      {/* Account List */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="retirement-accounts-list">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Your Accounts</h2>
        {accounts.length === 0 ? (
          <p className="text-center text-[#4A5568] py-8">No accounts yet. Add your first account above.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {accounts.map(account => (
              <div key={account.id} className="border border-slate-200 rounded-md p-4">
                <p className="text-sm font-semibold text-[#4A5568] mb-2">
                  {ACCOUNT_TYPES.find(t => t.value === account.account_type)?.label}
                </p>
                <p className="text-2xl font-semibold text-[#0A1128] ibm-plex mb-2">
                  ${account.balance.toLocaleString()}
                </p>
                <p className="text-sm text-[#4A5568]">
                  Annual Contribution: <span className="font-medium text-[#1A202C]">${account.annual_contribution.toLocaleString()}</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RetirementPlanner;